import { z } from 'zod';

// v1 ships observe-only: `observe` logs unmatched egress without dropping it.
// A future `enforce` mode (actually blocking traffic) is added here only once the
// drop-and-block script/provisioning path exists — exposing it before then would
// let callers pick an enforcement-sounding mode that silently still allows egress.
export const SANDBOX_EGRESS_MODES = ['off', 'observe'] as const;
export type SandboxEgressMode = (typeof SANDBOX_EGRESS_MODES)[number];

export const SandboxEgressModeSchema = z.enum(SANDBOX_EGRESS_MODES);

const domainPattern =
  /^(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;

const ipv4CidrPattern =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\/(?:[0-9]|[12]\d|3[0-2])$/;

const ipv6CidrPattern = /^(?:[0-9a-fA-F:]+)\/(?:[0-9]|[1-9]\d|1[01]\d|12[0-8])$/;

export const SandboxEgressAllowlistEntrySchema = z
  .object({
    kind: z.enum(['domain', 'cidr']),
    value: z.string().trim().min(1).max(253),
    note: z.string().trim().max(500).optional(),
    addedBy: z.string().uuid().optional(),
    addedAt: z.string().datetime().optional(),
  })
  .superRefine((entry, ctx) => {
    if (entry.kind === 'domain' && !domainPattern.test(entry.value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'domain allowlist entries must be fully qualified domain names',
      });
    }

    if (
      entry.kind === 'cidr' &&
      !ipv4CidrPattern.test(entry.value) &&
      !ipv6CidrPattern.test(entry.value)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'cidr allowlist entries must be IPv4 or IPv6 CIDR ranges',
      });
    }
  });

export const SandboxEgressAllowlistSchema = z
  .array(SandboxEgressAllowlistEntrySchema)
  .max(200);

export type SandboxEgressAllowlistEntry = z.infer<
  typeof SandboxEgressAllowlistEntrySchema
>;
export type SandboxEgressAllowlist = z.infer<typeof SandboxEgressAllowlistSchema>;
